// @flow
import { useDispatch } from 'react-redux';

import { Portal } from '@components/Portal';

import FormattedPrice from '@components/FormattedPrice';

import { shapeNames } from '@src/utils/displayMaps';
import { fullSizeShapeImagePath } from '@src/utils/images';

import { detailPath, searchPath } from '@src/app_routes/diamonds';

import css from '@styles/dyorDetail';

import type { DyorDiamondType } from '@types/diamond';

type TImageProps = { diamond: DyorDiamondType };
type TContentProps = TImageProps;
type TDescriptionProps = TImageProps;
type TPriceProps = TImageProps;
type TLinksProps = TImageProps;

type PropsType = {
  show: boolean,
  diamond: DyorDiamondType,
  chainPosition: number,
  containerRef: HTMLElement
};

function Image({ diamond }: TImageProps) {
  return (
    <div className={css.detailImage}>
      <img src={fullSizeShapeImagePath(diamond)} alt='Diamond image' />
    </div>
  );
}

function Content({ diamond }: TContentProps) {
  return (
    <div>
      <Description diamond={diamond} />
      <Price diamond={diamond} />
      <Links diamond={diamond} />
    </div>
  );
}

function Description({ diamond }: TDescriptionProps) {
  const { carat, color, clarity } = diamond;
  const shape = shapeNames[diamond.shape];
  const cut = diamond.final_cut_grade;

  return (
    <p>
      {carat} {shape}, {cut} Cut, {color} Color, {clarity} Clarity
    </p>
  );
}

function Price({ diamond }: TPriceProps) {
  return (
    <p className={css.detailPrice}>
      <FormattedPrice value={diamond.retail_price} />
      <ClearLink />
    </p>
  );
}

function ClearLink() {
  const dispatch = useDispatch();
  const onClearClick = React.useCallback(() => {
    dispatch({
      type: 'CLEAR_DYOR_DIAMOND',
      callback: () => (window.location = searchPath())
    });
  }, []);

  return (
    <a onClick={onClearClick} className={css.deleteLink}>
      delete
    </a>
  );
}

function Links({ diamond }: TLinksProps) {
  return (
    <div className={css.links}>
      <a className={css.viewLink} href={detailPath(diamond)}>
        VIEW
      </a>
      <a className={css.changeLink} href={searchPath()}>
        CHANGE
      </a>
    </div>
  );
}

export default React.forwardRef(
  ({ show, diamond, chainPosition, containerRef }: PropsType, ref) => {
    if (!show || !diamond) return null;

    return (
      <Portal htmlId='dyor-detail-popover'>
        <div
          className={css.detail({
            chainPosition,
            containerWidth: containerRef.current.offsetWidth
          })}
          ref={ref}
        >
          <Image diamond={diamond} />
          <Content diamond={diamond} />
        </div>
      </Portal>
    );
  }
);
